import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, CreditCard, Truck, Check, ArrowLeft, Store, Trash2, Plus, MapPin, Loader2, AlertTriangle, X, Percent, ChevronDown, ChevronUp, Printer } from 'lucide-react';
import { useCart, stockCap } from '../../context/CartContext';
import { CARRIERS, CASH_CARRY_LOCATIONS } from '../../data/mockPortalData';
import { nexiService } from '../../services/nexiService';
import { ProductThumb } from '../../components/product/ProductThumb';
import { HoverPreview } from '../../components/product/HoverPreview';
import { CarrierLogo } from '../../components/product/CarrierLogo';
import { VettoreLogo } from '../../components/portal/VettoreLogo';
import { PORTO_ASSEGNATO } from '../../utils/condizioniTrasporto';
import { QtyInput } from '../../components/product/QtyInput';
import { BoxNudge } from '../../components/product/BoxNudge';
import { StockWarning } from '../../components/product/StockWarning';
import { SAMPLE_PRODUCTS } from '../../data/siteData';
import { toast } from 'sonner';
import { orderService } from '../../services/orderService';
import { productService } from '../../services/productService';
import { useAuth } from '../../context/AuthContext';
import { useMyAnagrafica, invalidaAnagrafica } from '../../hooks/useMyAnagrafica';
import { useContatto } from '../../hooks/useContatto';
import { useMyStockAlerts } from '../../hooks/useMyStockAlerts';
import { useCartQuote } from '../../hooks/useCartQuote';
import { PendingPaymentBanner } from '../../components/portal/PendingPaymentBanner';
import api from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { ID_PAGAMENTO, PAGAMENTI_IMMEDIATI, descriviPagamento, type MetodoPagamento } from '../../utils/pagamenti';
import { articoliEPezzi } from '../../utils/articoliEPezzi';
import { ProvinciaSelect } from '../../components/form/ProvinciaSelect';

interface AddressEntry {
  id: string;
  label: string;
  nominativo: string;
  telefono: string;
  address: string;
  city: string;
  province: string;
  cap: string;
  /** Destinazione NTS (`dd_coddest`), 0 per la sede: va sull'ordine (API-112).
   *  `null` per l'indirizzo nuovo di un Contatto, che su NTS non esiste ancora. */
  coddest: number | null;
  /** Indirizzo nuovo di un Contatto, tenuto nella pagina: il backend lo salva
   *  fra le destinazioni del Cliente appena creato e ne mette il codice
   *  sull'ordine (`indirizzoSpedizione.nuovaDestinazione`). */
  nuovaDestinazione?: boolean;
  isDefault: boolean;
}

export default function CheckoutPage() {
  const { items, totalItems, clearCart, updateQty, removeItem } = useCart();
  const { user, canPlaceOrder, ricaricaUtente } = useAuth();
  const { data: anagrafica, loading: anagraficaLoading } = useMyAnagrafica();
  // Contatto: il primo ordine crea anche il Cliente su NTS (contatti-lead, 03).
  const contatto = useContatto();
  const { addAlert } = useMyStockAlerts();
  const [step, setStep] = useState<'review' | 'confirmed'>('review');
  const [coupon, setCoupon] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  /** Il numero web, sotto quello del gestionale (INT-134). */
  const [orderNumberWeb, setOrderNumberWeb] = useState<string | null>(null);
  // Il dettaglio dell'ordine su NTS, da stampare dalla conferma (#6).
  const [orderDetailPath, setOrderDetailPath] = useState<string | null>(null);
  const [shippingMethod, setShippingMethod] = useState<'corriere' | 'ritiro'>('corriere');
  const [addresses, setAddresses] = useState<AddressEntry[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<string | undefined>(undefined);
  const [selectedCarrier, setSelectedCarrier] = useState('bartolini');
  const [pagamentiAmmessi, setPagamentiAmmessi] = useState<MetodoPagamento[]>(PAGAMENTI_IMMEDIATI);
  const [defaultPayment, setDefaultPayment] = useState<MetodoPagamento>('bonifico_anticipato');
  const [ntsCarrierLabel, setNtsCarrierLabel] = useState<string | null>(null);
  const [selectedCashCarry, setSelectedCashCarry] = useState(CASH_CARRY_LOCATIONS[0]?.id);
  const [paymentMethod, setPaymentMethod] = useState<MetodoPagamento>('bonifico_anticipato');
  const navigate = useNavigate();
  // Quantità digitata oltre la disponibilità: la riga resta al massimo
  // ordinabile, e la richiesta serve a StockWarning per l'avviso sul resto.
  const [stockWarn, setStockWarn] = useState<{ sku: string; requested: number } | null>(null);
  const [expandedModels, setExpandedModels] = useState<Set<string>>(new Set());
  const [expandedOem, setExpandedOem] = useState<Set<string>>(new Set());
  // Nuovo indirizzo form
  const [showNewAddr, setShowNewAddr] = useState(false);
  const [newAddr, setNewAddr] = useState({ label: '', nominativo: '', telefono: '', address: '', city: '', province: '', cap: '' });
  const [shippingNote, setShippingNote] = useState('');

  // Carica indirizzi e defaults da NTS anagrafica
  const anagraficaLoadedRef = useRef(false);
  useEffect(() => {
    if (!anagrafica || anagraficaLoadedRef.current) return;
    anagraficaLoadedRef.current = true;

    // Costruisci lista indirizzi: sede legale + destinazioni NTS
    const built: AddressEntry[] = [];
    // Predefinita: `an_destin`; 0 o una destinazione non mostrata = la sede.
    const pref = anagrafica.destinazionePreferita ?? 0;
    const prefMostrata = (anagrafica.destinazioni || []).some(d => d.dd_coddest === pref && (d.inddest || d.citta));

    // Sede legale come primo indirizzo
    if (anagrafica.indirizzo && anagrafica.citta) {
      built.push({
        id: 'nts-sede',
        label: 'Sede Legale',
        nominativo: anagrafica.ragioneSociale,
        telefono: anagrafica.telefono || anagrafica.cellulare || '',
        address: anagrafica.indirizzo,
        city: anagrafica.citta,
        province: anagrafica.provincia || '',
        cap: anagrafica.cap || '',
        coddest: 0,
        isDefault: !prefMostrata,
      });
    }

    // Destinazioni alternative NTS
    (anagrafica.destinazioni || []).forEach((dest, idx) => {
      if (!dest.inddest && !dest.citta) return;
      if (dest.dd_coddest == null) return;
      const isDefault = prefMostrata && dest.dd_coddest === pref;
      built.push({
        id: `nts-dest-${dest.dd_coddest ?? idx}`,
        label: dest.dd_nomdest || `Destinazione ${idx + 1}`,
        nominativo: dest.dd_nomdest || anagrafica.ragioneSociale,
        telefono: dest.telef || '',
        address: dest.inddest || '',
        city: dest.citta || '',
        province: dest.prov || '',
        cap: dest.cap || '',
        coddest: dest.dd_coddest,
        isDefault,
      });
    });

    if (built.length > 0) {
      setAddresses(built);
      const def = built.find(a => a.isDefault) || built[0];
      setSelectedAddress(def.id);
    }

    // Pagamento: quelli ammessi per il conto; preselezionato quello
    // delle Preferenze se ancora ammesso, altrimenti quello dell'anagrafica.
    const ammessi = anagrafica.pagamentiAmmessi?.length ? anagrafica.pagamentiAmmessi : PAGAMENTI_IMMEDIATI;
    const daAnagrafica = anagrafica.pagamentoPredefinito && ammessi.includes(anagrafica.pagamentoPredefinito)
      ? anagrafica.pagamentoPredefinito : ammessi[0];
    setPagamentiAmmessi(ammessi);
    setDefaultPayment(daAnagrafica);
    setPaymentMethod(daAnagrafica);
    api.get<{ checkout?: { modalitaPagamentoPreferita?: MetodoPagamento | null } }>('/api/clienti/me/preferenze')
      .then(r => {
        const preferito = r.data?.checkout?.modalitaPagamentoPreferita;
        if (!preferito || !ammessi.includes(preferito)) return;
        setDefaultPayment(preferito);
        // Non sovrascrive una scelta già fatta mentre la richiesta era in volo.
        setPaymentMethod(cur => (cur === daAnagrafica ? preferito : cur));
      })
      .catch(() => {});

    // Il Vettore del Cliente si mostra solo con Porto Assegnato, come nelle
    // condizioni commerciali (``.scratch/condizioni-trasporto/spec.md``).
    if (anagrafica.vettoreLabel && String(anagrafica.porto ?? '') === PORTO_ASSEGNATO) {
      setNtsCarrierLabel(anagrafica.vettoreLabel);
    }
  }, [anagrafica]);

  const activeCarriers = CARRIERS.filter(c => c.active);

  // Il contrassegno cambia solo nome e condizione NTS: al corriere o al ritiro.
  const ritiro = shippingMethod !== 'corriere';

  // Solo il Titolare gestisce gli indirizzi di spedizione (CONTEXT.md).
  const puoAggiungereIndirizzi = user?.ruolo === 'cliente';
  const [savingAddr, setSavingAddr] = useState(false);

  /** Salva il nuovo indirizzo fra le destinazioni del Cliente su NTS, come
   *  dalla rubrica, e lo seleziona: l'ordine ne porta il codice. Un Contatto
   *  non ha ancora destinazioni NTS (`POST /me/destinazioni` gli risponde
   *  409): l'indirizzo resta nella pagina e parte con l'ordine. */
  const addNewAddress = async () => {
    if (!newAddr.nominativo || !newAddr.address || !newAddr.city || !newAddr.cap || !newAddr.province) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    if (contatto) {
      const entry: AddressEntry = {
        id: `nuovo-${Date.now()}`,
        label: newAddr.nominativo,
        nominativo: newAddr.nominativo,
        telefono: newAddr.telefono,
        address: newAddr.address,
        city: newAddr.city,
        province: newAddr.province,
        cap: newAddr.cap,
        coddest: null,
        nuovaDestinazione: true,
        isDefault: false,
      };
      setAddresses(prev => [...prev, entry]);
      setSelectedAddress(entry.id);
      setNewAddr({ label: '', nominativo: '', telefono: '', address: '', city: '', province: '', cap: '' });
      setShowNewAddr(false);
      toast.success(`Indirizzo "${entry.label}" aggiunto: lo salviamo fra i tuoi indirizzi con l'ordine`);
      return;
    }
    setSavingAddr(true);
    try {
      const { data } = await api.post<{ dd_coddest: number }>('/api/clienti/me/destinazioni', {
        dd_nomdest: newAddr.nominativo,
        dd_inddest: newAddr.address,
        dd_capdest: newAddr.cap,
        dd_locdest: newAddr.city,
        dd_prodest: newAddr.province,
        dd_telef: newAddr.telefono || null,
      });
      const entry: AddressEntry = {
        id: `nts-dest-${data.dd_coddest}`,
        label: newAddr.nominativo,
        nominativo: newAddr.nominativo,
        telefono: newAddr.telefono,
        address: newAddr.address,
        city: newAddr.city,
        province: newAddr.province,
        cap: newAddr.cap,
        coddest: data.dd_coddest,
        isDefault: false,
      };
      setAddresses(prev => [...prev, entry]);
      setSelectedAddress(entry.id);
      setNewAddr({ label: '', nominativo: '', telefono: '', address: '', city: '', province: '', cap: '' });
      setShowNewAddr(false);
      toast.success(`Indirizzo "${entry.label}" salvato fra i tuoi indirizzi`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Impossibile salvare l'indirizzo. Riprova."));
    } finally {
      setSavingAddr(false);
    }
  };

  // Modelli compatibili per riga carrello — dati REALI da abbinatore_associazioni
  // (stessa fonte di ProductDetailPage), non più dal mock ABBINAMENTI. Fetch
  // una volta per sku (richiesta utente 21/02/2026: mostrare modelli
  // espandibili, raggruppati per marca, come da esempio del vecchio sito).
  type CompatModel = { id: string; nome: string; marca: string };
  const [compatModelsBySku, setCompatModelsBySku] = useState<Record<string, CompatModel[]>>({});
  const fetchedSkusRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const toFetch = items.map(i => i.sku).filter(sku => !fetchedSkusRef.current.has(sku));
    if (toFetch.length === 0) return;
    toFetch.forEach(sku => fetchedSkusRef.current.add(sku));
    let cancelled = false;
    Promise.all(toFetch.map(sku =>
      productService.compatibleModels(sku)
        .then((list): [string, CompatModel[]] => [sku, list])
        .catch((): [string, CompatModel[]] => [sku, []])
    )).then(results => {
      if (cancelled) return;
      setCompatModelsBySku(prev => {
        const next = { ...prev };
        results.forEach(([sku, list]) => { next[sku] = list; });
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [items]);

  // Codici OEM reali dell'articolo (bug utente 26/02/2026: la colonna OEM
  // mostrava il mock PRODUCT_OEM_CODES, che per gli SKU reali ricadeva sul
  // fallback [sku] duplicando la colonna "Codice"). Fetch batch via
  // productService.list({skus}) — stessa fonte usata da ProductDetailPage
  // (campo Product.oemCodes, popolato da NTS).
  const [oemCodesBySku, setOemCodesBySku] = useState<Record<string, string[]>>({});
  const fetchedOemSkusRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const toFetch = items.map(i => i.sku).filter(sku => !fetchedOemSkusRef.current.has(sku));
    if (toFetch.length === 0) return;
    toFetch.forEach(sku => fetchedOemSkusRef.current.add(sku));
    let cancelled = false;
    productService.list({ skus: toFetch.join(',') }).then(res => {
      if (cancelled) return;
      setOemCodesBySku(prev => {
        const next = { ...prev };
        res.items.forEach(p => { next[p.sku] = p.oemCodes && p.oemCodes.length > 0 ? p.oemCodes : [p.sku]; });
        return next;
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [items]);

  // Prezzi delle righe come li scriverà il gestionale (il gestionale
  // comanda): listino con gli sconti NTS, e lo sconto scatola sulle sole
  // scatole intere (i pezzi sciolti a prezzo pieno), come il portale.
  const quote = useCartQuote(items, !!user);
  const imponibile = quote.imponibile;
  // Totale a listino, prima degli sconti (per evidenziare il risparmio nel riepilogo)
  const cartTotal = quote.totaleListino;

  // Soglia spedizione gratuita per-cliente (an_hhegimportominimo nel gestionale,
  // richiesta utente 24/02/2026) — nessun fallback globale: se il cliente non
  // ha questo valore impostato, nessun messaggio viene mostrato.
  const freeShippingThreshold = anagrafica?.trasportoMinimo && anagrafica.trasportoMinimo > 0
    ? anagrafica.trasportoMinimo
    : null;

  // Righe scontate dal gestionale, per il riepilogo ordine
  const scontiRows = useMemo(() => items
    .map(item => {
      const r = quote.riga(item);
      if (!r || r.percentualeSconto <= 0) return null;
      return { sku: item.sku, title: item.title, pct: r.scatola?.sconto ?? r.percentualeSconto,
               boxes: r.scatola?.scatole ?? null, savings: r.prezzo * item.qty - r.totaleLinea };
    })
    .filter((r): r is { sku: string; title: string; pct: number; boxes: number | null; savings: number } => r !== null),
  [items, quote]);

  // Riepilogo sconti — di default CHIUSO (richiesta utente 25/02/2026),
  // il cliente lo espande solo se vuole vedere il dettaglio riga per riga.
  const [scontiExpanded, setScontiExpanded] = useState(false);
  const totalSavings = useMemo(() => scontiRows.reduce((sum, r) => sum + r.savings, 0), [scontiRows]);

  const [submitting, setSubmitting] = useState(false);
  const [mismatchInfo, setMismatchInfo] = useState<Array<{
    sku: string; title: string; requestedQty: number; availableStock: number;
    requestedPrice: number; currentPrice: number; reason: string;
  }> | null>(null);
  const handleConfirm = () => {
    // Rete di sicurezza: con `canPlaceOrder === false` il pulsante non è
    // nemmeno renderizzato, ma la guardia resta perché il costo di un
    // ordine inviato per errore è un 403 a fine flusso.
    if (!canPlaceOrder) return;
    if (paymentMethod === 'carta') { void startCardPayment(); return; }
    void completeOrder();
  };

  /** Costruisce il corpo dell'ordine, uguale per il flusso carta e per gli
   *  altri metodi: l'unica differenza è chi lo invia e quando. */
  const buildOrderPayload = (confermaIncongruenze: boolean) => {
    const selectedAddr = addresses.find(a => a.id === selectedAddress) || addresses[0];
    const idCondizionePagamento = ID_PAGAMENTO[paymentMethod];
    const idModalitaSpedizione = shippingMethod === 'corriere' ? 1 : 2;

    return {
      idCliente: user?.idCliente,
      // `prezzoUnitario` è il listino: lo sconto lo applica NTS sulla riga.
      items: items.map(item => {
        const r = quote.riga(item);
        return {
          sku: item.sku,
          title: item.title,
          brand: item.brand,
          color: item.color || '-',
          qty: item.qty,
          prezzoUnitario: r?.prezzo ?? 0,
          percentualeSconto: r?.percentualeSconto ?? 0,
          totaleLinea: r?.totaleLinea ?? 0,
        };
      }),
      indirizzoSpedizione: {
        label: selectedAddr?.label,
        nominativo: selectedAddr?.nominativo || selectedAddr?.label || '',
        telefono: selectedAddr?.telefono || undefined,
        address: selectedAddr?.address || '',
        city: selectedAddr?.city || '',
        province: selectedAddr?.province || undefined,
        cap: selectedAddr?.cap || '',
        // L'indirizzo nuovo di un Contatto non ha codice: lo assegna NTS
        // quando il backend lo salva, dopo aver creato il Cliente.
        ...(selectedAddr?.nuovaDestinazione
          ? { nuovaDestinazione: true }
          : { coddest: selectedAddr?.coddest ?? undefined }),
      },
      idModalitaSpedizione,
      idCondizionePagamento,
      imponibile: Math.round(imponibile * 100) / 100,
      // Il portale non calcola l'IVA (INT-130): il campo resta e vale 0.
      iva: 0,
      speseSpedizione: 0,
      note: shippingNote || '',
      coupon: coupon || undefined,
      confermaIncongruenze,
    };
  };

  /**
   * Carta: il backend crea l'ordine sul gestionale, ne legge il totale
   * documento e ci restituisce il form da inoltrare a Nexi. Il carrello
   * **non** viene svuotato qui: lo sarà solo a pagamento riuscito, così chi
   * abbandona in cassa lo ritrova intatto.
   */
  const startCardPayment = async (confermaIncongruenze: boolean = false) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = buildOrderPayload(confermaIncongruenze);
      const totale = Math.round((payload.imponibile + payload.speseSpedizione) * 100) / 100;
      // Nessuna descrizione da qui: il numero d'ordine non esiste ancora —
      // lo assegna il gestionale dentro /avvia. La compone il backend, che
      // lo conosce. Passandola da qui finiva sull'estratto conto del Cliente
      // il suo codice cliente ("Ordine DTK 9010079") invece del numero
      // dell'ordine, che è ciò con cui poi chiama l'assistenza.
      const avvio = await nexiService.avvia(payload, totale);
      navigate('/pagamento/redirect', { state: { action: avvio.action, fields: avvio.fields } });
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.mismatches) {
        setMismatchInfo(detail.mismatches);
      } else if (status === 409 && detail?.codTrans) {
        // Il gateway dice che questo ordine risulta già pagato: mandiamo il
        // Cliente all'esito invece di fargli ritentare un addebito.
        navigate(`/pagamento/esito?codTrans=${detail.codTrans}`);
        return;
      } else if (status === 409 && detail?.numeroOrdine) {
        // L'ordine esiste sul gestionale a un prezzo diverso e lo sistema il
        // backoffice: riprovare ne creerebbe un secondo.
        toast.error(detail.message, { duration: 15000 });
        clearCart();
        navigate('/area-clienti');
        return;
      } else {
        toast.error(
          (typeof detail === 'string' && detail) ||
          detail?.message ||
          'Impossibile avviare il pagamento con carta'
        );
      }
      setSubmitting(false);
    }
  };

  /** Dopo il primo ordine di un Contatto: anagrafica e profilo cambiano sul
   *  server (nasce il Cliente), si scarta la cache e si rilegge `/me`. */
  const aggiornaDopoPrimoOrdine = () => {
    invalidaAnagrafica();
    void ricaricaUtente();
  };

  const completeOrder = async (confermaIncongruenze: boolean = false) => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const created = await orderService.create(buildOrderPayload(confermaIncongruenze) as any);
      // Il numero che il Cliente ritrova in "I miei ordini" (INT-134).
      setOrderNumber(created.numeroGestionale || created.numeroOrdine || `2026-${created.idOrdine}`);
      setOrderNumberWeb(created.numeroGestionale ? created.numeroOrdine : null);
      const k = created.erp?.key;
      setOrderDetailPath(k ? `/area-clienti/ordini/${k.et_tipork}/${k.et_anno}/${encodeURIComponent(k.et_serie)}/${k.et_numdoc}` : null);
      setStep('confirmed');
      clearCart();
      toast.success('Ordine inviato al gestionale!');
      // Il primo ordine ha creato il Cliente: si rilegge l'utente, così
      // `contatto` torna falso e il menu mostra di nuovo tutto.
      if (contatto) aggiornaDopoPrimoOrdine();
    } catch (err: any) {
      // Il Cliente può essere nato anche se l'ordine poi è fallito: l'account
      // non è più un Contatto, e il menu va aggiornato comunque.
      if (contatto) aggiornaDopoPrimoOrdine();
      if (err?.response?.status === 409 && err?.response?.data?.detail?.mismatches) {
        setMismatchInfo(err.response.data.detail.mismatches);
          setSubmitting(false);
        return;
      }
      const detail = err?.response?.data?.detail;
      const errMsg = typeof detail === 'string'
        ? detail
        : detail?.message
          ? `${detail.message}${detail.erp_errors ? ' — ' + detail.erp_errors.join(' · ') : ''}`
          : err?.message || 'Errore sconosciuto';
      toast.error('Ordine NON inviato: ' + errMsg, { duration: 10000 });
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0 && step !== 'confirmed') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 text-center">
        <ShoppingCart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h1 className="text-2xl font-semibold mb-2">Carrello vuoto</h1>
        <p className="text-muted-foreground mb-4">Aggiungi prodotti per procedere al checkout.</p>
        <div className="max-w-2xl mx-auto mb-4 text-left empty:hidden"><PendingPaymentBanner /></div>
        <Link to="/prodotti/toner" className="h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium inline-flex items-center">Vai al catalogo</Link>
      </div>
    );
  }

  if (step === 'confirmed') {
    // Bonifico all'ordine: la conferma, con le coordinate, la manda a mano
    // l'amministrazione; fino ad allora non si paga.
    const bonifico = paymentMethod === 'bonifico_anticipato';
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-background/95" data-testid="order-confirmed">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-emerald-600" /></div>
          <h1 className="text-2xl font-semibold mb-2">Ordine inviato!</h1>
          <p className="text-lg font-mono font-bold text-[hsl(214,58%,41%)]" data-testid="order-confirmed-numero">{orderNumber}</p>
          {orderNumberWeb && <p className="text-xs font-mono text-muted-foreground">web {orderNumberWeb}</p>}
          <div className="mb-2" />
          {bonifico ? (
            <div className="max-w-md mx-auto mb-6 text-left text-sm rounded-lg border border-amber-300 bg-amber-50 px-4 py-3" data-testid="order-confirmed-bonifico">
              <p className="font-semibold mb-1">{"Pagamento con bonifico all'ordine"}</p>
              <p>{"Riceverai dalla nostra amministrazione la conferma d'ordine con le coordinate per il bonifico. Attendere la conferma definitiva dell'ordine prima di effettuare il pagamento."}</p>
            </div>
          ) : (
            <p className="text-muted-foreground mb-6">{"Riceverai una conferma via email. L'ordine sar\u00E0 visibile nell'area ordini."}</p>
          )}
          <div className="flex items-center justify-center gap-3 flex-wrap">
            {orderDetailPath && (
              <Link to={`${orderDetailPath}?stampa=1`} data-testid="order-confirmed-print"
                className="h-9 px-4 rounded-lg border text-sm font-medium inline-flex items-center gap-1.5">
                <Printer className="w-4 h-4" /> Stampa ordine
              </Link>
            )}
            <Link to="/area-clienti/ordini" className="h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium inline-flex items-center">Vai agli ordini</Link>
            <Link to="/" className="h-9 px-4 rounded-lg border text-sm font-medium inline-flex items-center">Torna alla home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">Carrello</h1>
          <Link to="/prodotti/toner" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-3.5 h-3.5" /> Torna al catalogo</Link>
        </div>

        <div className="mb-6 empty:hidden"><PendingPaymentBanner /></div>

        {/* ===== Dettaglio Carrello — tabella HTML full-width ===== */}
        <div className="border border-gray-300 bg-white overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="cart-table">
              <thead>
                <tr className="bg-[hsl(214,64%,29%)] text-xs font-semibold text-white uppercase tracking-wider border-b divide-x divide-white/15">
                  <th className="py-1.5 px-3 text-center w-[40px]"></th>
                  <th className="py-1.5 px-3 text-center" data-testid="col-codice">Codice</th>
                  <th className="py-1.5 px-3 text-center" data-testid="col-marca">Marca</th>
                  <th className="py-1.5 px-3 text-center" data-testid="col-oem">Oem</th>
                  <th className="py-1.5 px-3 text-center" data-testid="col-descrizione">Descrizione</th>
                  <th className="py-1.5 px-3 text-center" data-testid="col-modelli">Modelli</th>
                  <th className="py-1.5 px-2 text-center w-[50px]" data-testid="col-kit">Kit</th>
                  <th className="py-1.5 px-2 text-center w-[50px]" data-testid="col-conf">Conf.</th>
                  <th className="py-1.5 px-2 text-center w-[140px]" data-testid="col-qta">Q.t&agrave;</th>
                  <th className="py-1.5 px-3 text-center w-[90px]" data-testid="col-prezzo">Prezzo</th>
                  <th className="py-1.5 px-3 text-center w-[90px]" data-testid="col-totale">Totale</th>
                  <th className="py-1.5 px-2 w-[36px]"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const prod = SAMPLE_PRODUCTS.find(p => p.sku === item.sku);
                  const oemCode = prod?.codiceBarcode || 'N.P.N.';
                  const isNPN = oemCode === 'N.P.N.';
                  const models = compatModelsBySku[item.sku] || [];
                  const isModelsExpanded = expandedModels.has(item.sku);
                  const riga = quote.riga(item);
                  // Disponibilità reale della riga (esistenza − impegnato),
                  // aggiornata al caricamento del carrello. ``null`` = non tracciata.
                  const stock = stockCap(item.stock);
                  const warn = stock !== null && stockWarn?.sku === item.sku && stockWarn.requested > stock
                    ? stockWarn : null;

                  return (
                    <React.Fragment key={item.sku}>
                    <tr
                      className={`border-b last:border-b-0 divide-x divide-gray-200 transition-colors bg-white hover:bg-[hsl(var(--dtk-canvas))]/60`}
                      data-testid="checkout-item-row"
                    >
                      {/* Thumb */}
                      <td className="py-1.5 px-3 align-middle text-center">
                        <HoverPreview
                          sku={item.sku}
                          category={prod?.category || 'altro'}
                          title={prod?.title || item.title}
                          brand={item.brand}
                        >
                          <ProductThumb sku={item.sku} brand={item.brand} color={prod?.color} size="sm" className="w-8 h-8 mx-auto" />
                        </HoverPreview>
                      </td>

                      {/* Codice (DTK numerico) */}
                      <td className="py-1.5 px-3 align-middle text-center">
                        <span className="font-mono text-sm text-foreground">{prod?.dtkCodice || item.sku}</span>
                      </td>

                      {/* Marca */}
                      <td className="py-1.5 px-3 align-middle text-center">
                        <span className="text-sm text-foreground">{item.brand}</span>
                      </td>

                      {/* Oem — supporta codici multipli */}
                      <td className="py-1.5 px-3 align-middle text-center">
                        {(() => {
                          const oemList = oemCodesBySku[item.sku] || [item.sku];
                          const SHOW_OEM = 1;
                          const isOemExpanded = expandedOem.has(item.sku);
                          const shown = isOemExpanded ? oemList : oemList.slice(0, SHOW_OEM);
                          const hiddenOem = oemList.length - SHOW_OEM;
                          return (
                            <div className="text-sm leading-snug">
                              {shown.map((code, i) => (
                                <div key={i} className="font-mono text-foreground">{code}</div>
                              ))}
                              {!isOemExpanded && hiddenOem > 0 && (
                                <button
                                  onClick={() => setExpandedOem(prev => new Set(Array.from(prev).concat(item.sku)))}
                                  className="text-sm text-muted-foreground hover:underline"
                                >
                                  +{hiddenOem} altri
                                </button>
                              )}
                              {isOemExpanded && oemList.length > SHOW_OEM && (
                                <button
                                  onClick={() => setExpandedOem(prev => { const s = new Set(prev); s.delete(item.sku); return s; })}
                                  className="text-sm text-muted-foreground hover:underline"
                                >
                                  mostra meno
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </td>

                      {/* Descrizione */}
                      <td className="py-1.5 px-3 align-middle text-center">
                        <div className="text-sm leading-snug">{item.title}</div>
                        {prod?.yield && <div className="text-sm text-muted-foreground">{prod.yield}</div>}
                        {(() => {
                          // Sconto scatola: solo le scatole intere, i pezzi sciolti a prezzo pieno.
                          const boxQty = item.boxQty ?? 0;
                          const pct = riga?.scatola?.sconto ?? item.boxDiscountPct ?? 0;
                          if (!riga || boxQty <= 1 || pct <= 0) return null;
                          const cadBox = riga.prezzo * (1 - pct / 100);
                          return (
                            <div className="relative group/boxi inline-flex items-center gap-0.5 mt-1">
                              <span className="text-xs text-emerald-700 font-medium">Sconto scatola: -{pct.toLocaleString('it-IT')}%</span>
                              <span className="w-3.5 h-3.5 rounded-full bg-[hsl(214,58%,41%)] text-white text-[8px] font-bold inline-flex items-center justify-center cursor-help">i</span>
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/boxi:block z-50">
                                <span className="bg-[hsl(214,58%,41%)] text-white rounded-lg p-2.5 shadow-lg text-[10px] leading-relaxed whitespace-nowrap block">
                                  <span className="block">Prezzo singolo: {'\u20AC'} {riga.prezzo.toFixed(2).replace('.', ',')}</span>
                                  <span className="block">Prezzo scatola: {'\u20AC'} {cadBox.toFixed(2).replace('.', ',')} cad.</span>
                                  <span className="block mt-1 pt-1 border-t border-white/30">Scatola: {boxQty} pz = {'\u20AC'} {(cadBox * boxQty).toFixed(2).replace('.', ',')}</span>
                                </span>
                              </span>
                            </div>
                          );
                        })()}
                      </td>

                      {/* Modelli — espandibile: mostra il primo modello +
                          caret, click apre riga full-width con l'elenco
                          completo raggruppato per marca (dati reali). */}
                      <td className="py-1.5 px-3 align-middle text-center">
                        {models.length === 0 ? (
                          <span className="text-muted-foreground text-sm">&mdash;</span>
                        ) : (
                          <button
                            onClick={() => setExpandedModels(prev => {
                              const s = new Set(prev);
                              isModelsExpanded ? s.delete(item.sku) : s.add(item.sku);
                              return s;
                            })}
                            className="inline-flex items-center gap-1 text-sm text-foreground hover:text-[hsl(214,58%,41%)] max-w-[170px]"
                            data-testid={`checkout-toggle-models-${item.sku}`}
                          >
                            <span className="truncate underline decoration-gray-300 underline-offset-2">{models[0].nome}</span>
                            {isModelsExpanded ? <ChevronUp className="w-3.5 h-3.5 flex-shrink-0 text-[hsl(214,58%,41%)]" /> : <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 text-[hsl(214,58%,41%)]" />}
                          </button>
                        )}
                      </td>

                      {/* Kit */}
                      <td className="py-1.5 px-2 align-middle text-center text-sm text-foreground">
                        {(prod as any)?.kit || ''}
                      </td>

                      {/* Conf. */}
                      <td className="py-1.5 px-2 align-middle text-center text-sm text-foreground">
                        1
                      </td>

                      {/* Q.t\u00E0 — QtyInput + StockWarning (identico al CartDrawer) */}
                      <td className="py-1.5 px-2 align-middle text-center">
                        <div className="relative flex flex-col items-center">
                          <QtyInput
                            value={item.qty}
                            onChange={(q) => updateQty(item.sku, q)}
                            max={stock !== null ? Math.min(stock, 999) : 999}
                            onExceedMax={(q) => {
                              if (stock === null) return;
                              updateQty(item.sku, stock);
                              if (q > stock && stock > 0) setStockWarn({ sku: item.sku, requested: q });
                            }}
                            size="sm"
                          />
                          {warn && stock !== null && (
                            <div className="relative">
                              <StockWarning
                                sku={item.sku}
                                qtyRequested={warn.requested}
                                stockAvailable={stock}
                                onOrderAvailable={() => {
                                  const missing = warn.requested - stock;
                                  updateQty(item.sku, stock);
                                  addAlert({ sku: item.sku, title: item.title, brand: item.brand, category: item.category, qtyWanted: missing }).catch(() => {});
                                  toast.success(`${stock} pz ordinati + alert per i restanti ${missing}`);
                                  setStockWarn(null);
                                }}
                                onAddAvailableOnly={() => {
                                  updateQty(item.sku, stock);
                                  toast.success(`Quantità aggiornata a ${stock} pz disponibili`);
                                  setStockWarn(null);
                                }}
                              />
                            </div>
                          )}
                          {/* BoxNudge — sconto scatola nel checkout */}
                          {riga && riga.prezzo > 0 && (
                            <BoxNudge sku={item.sku} qty={item.qty} unitPrice={riga.prezzo} onSetQty={(q) => updateQty(item.sku, q)} onBoxCompleted={(boxes, bq) => toast.success(`${boxes} scatola${boxes > 1 ? 'e' : ''} da ${bq} nel carrello!`)} />
                          )}
                        </div>
                      </td>
                      <td className="py-1.5 px-3 align-middle text-center tabular-nums text-sm">
                        {riga ? (
                          <>
                            {`\u20AC ${riga.prezzo.toFixed(2).replace('.', ',')}`}
                            {riga.percentualeSconto > 0 && (
                              <span className="block text-xs text-emerald-700 font-medium" data-testid="checkout-item-sconto">
                                -{riga.percentualeSconto.toLocaleString('it-IT')}%
                              </span>
                            )}
                          </>
                        ) : quote.nonVendibile(item) ? (
                          <span className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded border bg-red-50 text-red-700 border-red-200" data-testid="checkout-item-non-disponibile">
                            {quote.rimosso(item) ? 'Non più disponibile' : 'Non disponibile'}
                          </span>
                        ) : <span className="text-muted-foreground">&mdash;</span>}
                      </td>

                      {/* Totale riga */}
                      <td className="py-1.5 px-3 align-middle text-center tabular-nums text-sm">
                        {riga ? (
                          <span className="text-foreground">{'\u20AC'} {riga.totaleLinea.toFixed(2).replace('.', ',')}</span>
                        ) : <span className="text-muted-foreground">&mdash;</span>}
                      </td>

                      {/* Elimina */}
                      <td className="py-1.5 px-2 align-middle text-center">
                        <button
                          onClick={() => { removeItem(item.sku); toast.info(`${item.sku} rimosso`); }}
                          className="w-7 h-7 rounded flex items-center justify-center text-muted-foreground hover:bg-red-50 hover:text-red-500 transition-colors mx-auto"
                          data-testid="checkout-remove-item"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                    {isModelsExpanded && models.length > 0 && (() => {
                      const byBrand: Record<string, string[]> = {};
                      models.forEach(m => { (byBrand[m.marca] ||= []).push(m.nome); });
                      return (
                        <tr className="bg-[hsl(var(--dtk-canvas))] border-b" data-testid={`checkout-models-panel-${item.sku}`}>
                          <td colSpan={12} className="px-6 py-3.5">
                            <div className="text-sm font-semibold text-foreground mb-1.5">Modelli Compatibili:</div>
                            <div className="space-y-1.5">
                              {Object.entries(byBrand).map(([marca, nomi]) => (
                                <div key={marca} className="text-sm leading-relaxed">
                                  <span className="font-bold text-foreground">{marca}:</span>{' '}
                                  <span className="text-muted-foreground">{nomi.join(', ')}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      );
                    })()}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t bg-[hsl(var(--dtk-canvas))]">
                  <td colSpan={7} className="py-3 px-4 text-xs text-muted-foreground">
                    Tutti i prezzi si intendono iva esclusa
                  </td>
                  <td colSpan={5} className="py-3 px-4 text-right">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Totale Imponibile</span>
                    <span className="text-base font-bold text-[hsl(214,58%,41%)] tabular-nums ml-3" data-testid="checkout-table-total">
                      &euro; {imponibile.toFixed(2).replace('.', ',')}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {scontiRows.length > 0 && (
            <div className="border-t-2 border-emerald-200 bg-emerald-50/60" data-testid="cart-detail-sconti-summary">
              <button
                type="button"
                onClick={() => setScontiExpanded(v => !v)}
                className="w-full flex items-center justify-between flex-wrap gap-2 px-5 py-4 text-left hover:bg-emerald-100/40 transition-colors"
                data-testid="cart-detail-sconti-toggle"
              >
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                    <Percent className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-sm font-bold text-emerald-800">Sconti applicati</span>
                  <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5 tabular-nums">
                    &minus; &euro; {totalSavings.toFixed(2).replace('.', ',')}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-xs text-muted-foreground">
                    Subtotale ante sconto <span className="font-semibold text-foreground tabular-nums">€ {cartTotal.toFixed(2).replace('.', ',')}</span>
                  </div>
                  {scontiExpanded ? <ChevronUp className="w-4 h-4 text-emerald-700 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-emerald-700 flex-shrink-0" />}
                </div>
              </button>
              {scontiExpanded && (
                <div className="px-5 pb-4" data-testid="cart-detail-sconti-detail">
                  <div className="rounded-lg bg-white border border-emerald-100 overflow-hidden">
                    <table className="w-full text-[13px] border-collapse">
                      <thead>
                        <tr className="border-b bg-emerald-50/70" style={{ color: '#3a3a36' }}>
                          <th className="text-left font-semibold py-2 pl-3 pr-1.5">Cod. DTK</th>
                          <th className="text-left font-semibold py-2 pr-1.5">Descrizione</th>
                          <th className="text-right font-semibold py-2 pr-1.5">Sconto</th>
                          <th className="text-right font-semibold py-2 pr-1.5">Scatole</th>
                          <th className="text-right font-semibold py-2 pr-3">Sconto riga</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scontiRows.map(row => (
                          <tr key={row.sku} className="border-b border-dashed border-secondary last:border-0" data-testid={`cart-detail-sconti-row-${row.sku}`}>
                            <td className="py-2 pl-3 pr-1.5 font-mono font-semibold text-foreground whitespace-nowrap align-top">{row.sku}</td>
                            <td className="py-2 pr-1.5 align-top">
                              <div className="truncate max-w-[220px]" style={{ color: '#3a3a36' }} title={row.title}>{row.title}</div>
                            </td>
                            <td className="py-2 pr-1.5 text-right tabular-nums whitespace-nowrap align-top" style={{ color: '#3a3a36' }}>{row.pct.toLocaleString('it-IT')}%</td>
                            <td className="py-2 pr-1.5 text-right tabular-nums whitespace-nowrap align-top" style={{ color: '#3a3a36' }}>{row.boxes ? `×${row.boxes}` : '—'}</td>
                            <td className="py-2 pr-3 text-right tabular-nums font-bold text-emerald-700 whitespace-nowrap align-top">
                              − € {row.savings.toFixed(2).replace('.', ',')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-3 mt-3 border-t border-emerald-200">
                    <span className="text-sm font-semibold text-emerald-900">Prezzo finale IVA esclusa</span>
                    <span className="font-bold text-emerald-700 tabular-nums text-xl">
                      € {imponibile.toFixed(2).replace('.', ',')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Note per la spedizione */}
        <div className="rounded-xl border bg-white p-4 mb-6">
          <h2 className="text-sm font-semibold mb-2">Note per la spedizione</h2>
          <textarea
            value={shippingNote}
            onChange={e => setShippingNote(e.target.value)}
            placeholder="Aggiungi note alla spedizione"
            rows={1}
            className="w-full px-3 py-1.5 rounded-lg border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]"
            data-testid="checkout-shipping-note"
          />
        </div>

        {/* Nota trasporto */}
        <p className="text-sm text-muted-foreground text-center mb-8">
          Trasporto, condizioni e termini di pagamento come da accordi in essere.
        </p>

        {/* ===== Metodo Pagamento + Modalit\u00E0 Spedizione — side by side ===== */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Pagamento (col destra — ultimo step decisionale) */}
          <div className="rounded-xl border bg-white p-5 order-2">
            <h2 className="text-sm font-semibold mb-4 flex items-center gap-2"><CreditCard className="w-4 h-4" /> Metodo di Pagamento</h2>
            <div className="space-y-2">
              {/* I metodi ammessi per il conto (`pagamentiAmmessi`): la
                  condizione NTS del Cliente, contrassegno e bonifico
                  all'ordine. */}
              {pagamentiAmmessi.map(metodo => {
                const { label, descrizione } = descriviPagamento(metodo, {
                  condizione: anagrafica?.codicePagamentoLabel, ritiro,
                });
                // Test id storici del contrassegno: corriere e ritiro.
                const testId = metodo === 'contrassegno' ? (ritiro ? 'payment-cod-generic' : 'payment-cod-carrier') : `payment-${metodo}`;
                return (
                  <label key={metodo} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${paymentMethod === metodo ? 'bg-[hsl(210,40%,93%)]/30 border-[hsl(var(--dtk-focus))]' : 'hover:bg-secondary/40'}`} data-testid={`${testId}-label`}>
                    <input type="radio" name="payment" checked={paymentMethod === metodo} onChange={() => setPaymentMethod(metodo)} className="rounded-full" data-testid={`${testId}-radio`} />
                    <div className="flex-1">
                      <div className="text-sm font-medium flex items-center gap-2">
                        {label}
                        {defaultPayment === metodo && <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary font-medium">Predefinito</span>}
                        {/* Loghi reali dei circuiti, non etichette colorate: il
                            chip bianco serve perché entrambi gli asset hanno
                            sfondo trasparente e sparirebbero su fondo chiaro. */}
                        {/* Altezze diverse di proposito: il marchio VISA è largo
                            e basso (3,09:1), quello Mastercard quasi quadrato
                            (1,68:1). A parità di altezza il primo sfonderebbe il
                            chip e il secondo sembrerebbe minuscolo. */}
                        {metodo === 'carta' && (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="inline-flex items-center justify-center h-5 w-10 rounded border bg-white" data-testid="card-circuit-visa">
                              <img src="/payments/visa.png" alt="VISA" className="h-2.5 max-w-full object-contain" loading="lazy" />
                            </span>
                            <span className="inline-flex items-center justify-center h-5 w-10 rounded border bg-white" data-testid="card-circuit-mastercard">
                              <img src="/payments/mastercard.png" alt="Mastercard" className="h-3.5 max-w-full object-contain" loading="lazy" />
                            </span>
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-muted-foreground">{descrizione}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            {paymentMethod === 'carta' && (
              <div className="mt-3 pt-3 border-t">
                <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 flex items-center gap-2 text-xs text-blue-700">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                  {"Cliccando \"Conferma Ordine\" verrai reindirizzato alla pagina sicura del circuito di pagamento."}
                </div>
              </div>
            )}
          </div>

          {/* Spedizione (col sinistra — primo step decisionale) */}
          <div className="rounded-xl border bg-white p-5 order-1">
            <h2 className="text-sm font-semibold mb-4 flex items-center gap-2"><Truck className="w-4 h-4" /> Modalit&agrave; Spedizione</h2>
            <div className="space-y-2 mb-4">
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${shippingMethod === 'corriere' ? 'bg-[hsl(210,40%,93%)]/30 border-[hsl(var(--dtk-focus))]' : 'hover:bg-secondary/40'}`}>
                <input type="radio" name="shipping" checked={shippingMethod === 'corriere'} onChange={() => setShippingMethod('corriere')} className="rounded-full" />
                <div className="flex-1">
                  <div className="text-sm font-medium flex items-center gap-2">
                    Spedizione con Corriere
                    {shippingMethod === 'corriere' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary font-medium">Predefinito</span>}
                  </div>
                  <div className="text-[10px] text-muted-foreground">Consegna in 24/36 ore su tutto il territorio</div>
                </div>
              </label>
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${shippingMethod === 'ritiro' ? 'bg-[hsl(210,40%,93%)]/30 border-[hsl(var(--dtk-focus))]' : 'hover:bg-secondary/40'}`}>
                <input type="radio" name="shipping" checked={shippingMethod === 'ritiro'} onChange={() => setShippingMethod('ritiro')} className="rounded-full" />
                <div className="flex-1">
                  <div className="text-sm font-medium flex items-center gap-2">
                    Ritiro presso Cash &amp; Carry <Store className="w-3.5 h-3.5 text-muted-foreground" />
                    {shippingMethod === 'ritiro' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary font-medium">Predefinito</span>}
                  </div>
                  <div className="text-[10px] text-muted-foreground">Ritira di persona - Nessun costo di spedizione</div>
                </div>
              </label>
            </div>

            {shippingMethod === 'corriere' && freeShippingThreshold != null && (
              <div className="mb-4" data-testid="checkout-free-shipping-banner">
                {cartTotal >= freeShippingThreshold ? (
                  <div className="flex items-center gap-2 text-xs text-emerald-700 font-medium bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    <Truck className="w-3.5 h-3.5" /> Spedizione gratuita per il tuo ordine!
                  </div>
                ) : (
                  <div className="bg-secondary/40 rounded-lg px-3 py-2">
                    <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                      <span>Spedizione gratuita da {freeShippingThreshold}€ (soglia riservata a te)</span>
                      <span>Mancano {(freeShippingThreshold - cartTotal).toFixed(2).replace('.', ',')}€</span>
                    </div>
                    <div className="w-full h-1.5 rounded-full bg-secondary">
                      <div
                        className="h-full rounded-full bg-[hsl(var(--dtk-focus))] transition-all"
                        style={{ width: `${Math.min(100, (cartTotal / freeShippingThreshold) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {shippingMethod === 'corriere' && (
              <div className="space-y-4 pt-3 border-t">
                <div>
                  <label className="text-xs font-medium block mb-1.5">Corriere</label>
                  <div className="space-y-1.5">
                    {ntsCarrierLabel ? (
                      <div className="flex items-center gap-3 px-3 py-2 rounded-lg border bg-[hsl(210,40%,93%)]/30 border-[hsl(var(--dtk-focus))]">
                        <div className="flex-1 text-sm">
                          <VettoreLogo nome={ntsCarrierLabel} />
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary ml-2">Il tuo corriere</span>
                        </div>
                      </div>
                    ) : (
                      activeCarriers.filter(c => c.id === selectedCarrier).map(c => (
                        <div key={c.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border bg-[hsl(210,40%,93%)]/30 border-[hsl(var(--dtk-focus))]">
                          <CarrierLogo code={c.code} />
                          <div className="flex-1">
                            <span className="text-sm font-medium">{c.name}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary ml-2">Il tuo corriere</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium">Indirizzo di Spedizione</label>
                    {puoAggiungereIndirizzi && (
                      <button
                        onClick={() => setShowNewAddr(!showNewAddr)}
                        className="text-[10px] font-medium text-[hsl(214,58%,46%)] hover:text-[hsl(214,58%,41%)] flex items-center gap-1 transition-colors"
                        data-testid="add-address-btn"
                      >
                        <Plus className="w-3 h-3" /> Aggiungi indirizzo
                      </button>
                    )}
                  </div>

                  {/* Skeleton loading indirizzi NTS */}
                  {anagraficaLoading && (
                    <div className="animate-pulse space-y-2" data-testid="addresses-loading">
                      <div className="h-14 bg-secondary rounded-lg" />
                      <div className="h-14 bg-secondary rounded-lg w-4/5" />
                    </div>
                  )}

                  {/* Form nuovo indirizzo */}
                  {showNewAddr && puoAggiungereIndirizzi && (
                    <div className="rounded-lg border border-dashed border-[hsl(var(--dtk-focus))] bg-[hsl(var(--dtk-canvas))] p-4 mb-3 space-y-2" data-testid="new-address-form">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-[hsl(214,58%,41%)] mb-1">
                        <MapPin className="w-3.5 h-3.5" /> Nuovo indirizzo di spedizione
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          value={newAddr.nominativo}
                          onChange={e => setNewAddr(p => ({ ...p, nominativo: e.target.value }))}
                          placeholder="Nome destinazione *"
                          className="h-8 px-3 rounded-md border text-xs focus:outline-none focus:ring-1 focus:ring-[hsl(var(--dtk-focus))]"
                          data-testid="new-addr-nominativo"
                        />
                        <input
                          value={newAddr.telefono}
                          onChange={e => setNewAddr(p => ({ ...p, telefono: e.target.value }))}
                          placeholder="Telefono"
                          className="h-8 px-3 rounded-md border text-xs focus:outline-none focus:ring-1 focus:ring-[hsl(var(--dtk-focus))]"
                          data-testid="new-addr-telefono"
                        />
                      </div>
                      <input
                        value={newAddr.address}
                        onChange={e => setNewAddr(p => ({ ...p, address: e.target.value }))}
                        placeholder="Indirizzo (es. Via Roma 10) *"
                        className="w-full h-8 px-3 rounded-md border text-xs focus:outline-none focus:ring-1 focus:ring-[hsl(var(--dtk-focus))]"
                        data-testid="new-addr-address"
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <input
                          value={newAddr.city}
                          onChange={e => setNewAddr(p => ({ ...p, city: e.target.value }))}
                          placeholder="Citt&agrave; *"
                          className="h-8 px-3 rounded-md border text-xs focus:outline-none focus:ring-1 focus:ring-[hsl(var(--dtk-focus))]"
                          data-testid="new-addr-city"
                        />
                        <ProvinciaSelect
                          value={newAddr.province}
                          onChange={v => setNewAddr(p => ({ ...p, province: v }))}
                          size="sm"
                          placeholder="Prov. *"
                          testid="new-addr-province"
                        />
                        <input
                          value={newAddr.cap}
                          onChange={e => setNewAddr(p => ({ ...p, cap: e.target.value.replace(/\D/g, '').slice(0, 5) }))}
                          placeholder="CAP *"
                          maxLength={5}
                          className="h-8 px-3 rounded-md border text-xs focus:outline-none focus:ring-1 focus:ring-[hsl(var(--dtk-focus))]"
                          data-testid="new-addr-cap"
                        />
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={addNewAddress}
                          disabled={savingAddr}
                          className="h-8 px-4 rounded-md bg-[hsl(214,58%,41%)] text-white text-xs font-medium hover:bg-[hsl(214,58%,46%)] transition-colors disabled:opacity-60"
                          data-testid="save-new-address"
                        >
                          {savingAddr ? 'Salvataggio…' : 'Salva indirizzo'}
                        </button>
                        <button
                          onClick={() => { setShowNewAddr(false); setNewAddr({ label: '', nominativo: '', telefono: '', address: '', city: '', province: '', cap: '' }); }}
                          className="h-8 px-4 rounded-md border text-xs font-medium hover:bg-secondary/60 transition-colors"
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    {addresses.map(addr => (
                      <label key={addr.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedAddress === addr.id ? 'bg-[hsl(210,40%,93%)]/30 border-[hsl(var(--dtk-focus))]' : 'hover:bg-secondary/40'}`}>
                        <input type="radio" name="address" checked={selectedAddress === addr.id} onChange={() => setSelectedAddress(addr.id)} className="rounded-full mt-0.5" />
                        <div>
                          <div className="text-sm font-medium">{addr.label} {addr.isDefault && <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary ml-1">Predefinito</span>}</div>
                          {addr.nominativo && <div className="text-xs text-foreground">{addr.nominativo}{addr.telefono && <span className="text-muted-foreground ml-2">Tel: {addr.telefono}</span>}</div>}
                          <div className="text-xs text-muted-foreground">{addr.address}, {addr.cap} {addr.city} ({addr.province})</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {shippingMethod === 'ritiro' && (
              <div className="pt-3 border-t">
                <label className="text-xs font-medium block mb-1.5">Punto di Ritiro</label>
                {CASH_CARRY_LOCATIONS.filter(l => l.active).map(loc => (
                  <label key={loc.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedCashCarry === loc.id ? 'bg-[hsl(210,40%,93%)]/30 border-[hsl(var(--dtk-focus))]' : 'hover:bg-secondary/40'}`}>
                    <input type="radio" name="cashcarry" checked={selectedCashCarry === loc.id} onChange={() => setSelectedCashCarry(loc.id)} className="rounded-full mt-0.5" />
                    <div>
                      <div className="text-sm font-medium flex items-center gap-2"><Store className="w-3.5 h-3.5" /> {loc.name}</div>
                      <div className="text-xs text-muted-foreground">{loc.address}, {loc.city}</div>
                      <div className="text-xs text-muted-foreground">Tel: {loc.phone} | {loc.hours}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Coupon + Riepilogo + Conferma */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Coupon */}
          <div className="rounded-xl border bg-white p-5">
            <h2 className="text-sm font-semibold mb-3">Codice Promozionale</h2>
            <div className="flex gap-2">
              <input value={coupon} onChange={e => setCoupon(e.target.value)} placeholder="Inserisci codice" className="flex-1 h-9 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]" />
              <button className="h-9 px-4 rounded-lg border text-sm font-medium hover:bg-secondary/60">Applica</button>
            </div>
          </div>

          {/* Riepilogo + Totale + Conferma */}
          <div className="rounded-xl border bg-white p-5">
            <h2 className="text-sm font-semibold mb-3">Riepilogo Ordine</h2>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Articoli</span><span data-testid="checkout-articoli">{articoliEPezzi(items.length, totalItems)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Spedizione</span><span>{shippingMethod === 'ritiro' ? 'Ritiro C&C' : CARRIERS.find(c => c.id === selectedCarrier)?.code}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Pagamento</span><span className="text-right text-xs">{descriviPagamento(paymentMethod, { condizione: anagrafica?.codicePagamentoLabel, ritiro }).label}</span></div>
            </div>

            {scontiRows.length > 0 && (
              <div className="mt-4 pt-3 border-t" data-testid="checkout-sconti-summary">
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                  <span>Subtotale ante sconto</span>
                  <span className="tabular-nums">€ {cartTotal.toFixed(2).replace('.', ',')}</span>
                </div>
                <div className="text-sm font-semibold text-foreground mb-2">Sconti</div>
                <table className="w-full text-[13px] border-collapse">
                  <thead>
                    <tr className="border-b" style={{ color: '#3a3a36' }}>
                      <th className="text-left font-semibold pb-1.5 pr-1.5">Cod. DTK</th>
                      <th className="text-left font-semibold pb-1.5 pr-1.5">Descrizione</th>
                      <th className="text-right font-semibold pb-1.5 pr-1.5">Sconto</th>
                      <th className="text-right font-semibold pb-1.5 pr-1.5">Scatole</th>
                      <th className="text-right font-semibold pb-1.5">Sconto riga</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scontiRows.map(row => (
                      <tr key={row.sku} className="border-b border-dashed border-secondary last:border-0" data-testid={`checkout-sconti-row-${row.sku}`}>
                        <td className="py-2 pr-1.5 font-mono font-semibold text-foreground whitespace-nowrap align-top">{row.sku}</td>
                        <td className="py-2 pr-1.5 align-top">
                          <div className="truncate max-w-[110px]" style={{ color: '#3a3a36' }} title={row.title}>{row.title}</div>
                        </td>
                        <td className="py-2 pr-1.5 text-right tabular-nums whitespace-nowrap align-top" style={{ color: '#3a3a36' }}>{row.pct.toLocaleString('it-IT')}%</td>
                        <td className="py-2 pr-1.5 text-right tabular-nums whitespace-nowrap align-top" style={{ color: '#3a3a36' }}>{row.boxes ? `×${row.boxes}` : '—'}</td>
                        <td className="py-2 text-right tabular-nums font-bold text-foreground whitespace-nowrap align-top">
                          − € {row.savings.toFixed(2).replace('.', ',')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className={scontiRows.length > 0 ? 'mt-2 pt-3 border-t' : 'mt-4 pt-3 border-t'}>
              <div className="flex justify-between items-baseline">
                <span className="text-base font-bold">{scontiRows.length > 0 ? 'Prezzo finale' : 'Totale Imponibile'}</span>
                <span className={`font-bold text-[hsl(214,58%,41%)] tabular-nums ${scontiRows.length > 0 ? 'text-2xl' : 'text-xl'}`} data-testid="checkout-total">
                  &euro; {imponibile.toFixed(2).replace('.',',')}
                </span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Importo netto IVA esclusa</div>
            </div>
            {/* Un operatore senza il ruolo «Crea ordine» arrivava fin qui e
                riceveva il 403 solo dopo aver premuto Conferma. Il limite
                va detto prima, e il carrello che ha preparato resta
                valido: lo invia il titolare. */}
            {canPlaceOrder && quote.haNonVendibili && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800" data-testid="checkout-non-vendibili">
                Alcuni articoli non sono più disponibili o non sono acquistabili online: rimuovili dal carrello per procedere.
              </div>
            )}
            {canPlaceOrder && contatto && (
              <p className="mt-4 text-xs text-muted-foreground" data-testid="checkout-contatto-nota">
                Con questo primo ordine apriamo anche il tuo account cliente DTK.
              </p>
            )}
            {canPlaceOrder ? (
              <button onClick={handleConfirm} disabled={submitting || quote.loading || quote.haNonVendibili} data-testid="confirm-order-button"
                className="w-full h-11 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-[hsl(214,58%,46%)] transition-colors active:scale-[0.99] mt-4 disabled:opacity-60 disabled:cursor-wait">
                {submitting ? (
                  <><svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3"/><path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/></svg> Invio al gestionale…</>
                ) : (paymentMethod === 'carta' ? <><CreditCard className="w-4 h-4" /> Procedi al Pagamento</> : <><Truck className="w-4 h-4" /> Conferma Ordine</>)}
              </button>
            ) : (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="checkout-operatore-blocked">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-[13px] leading-relaxed text-amber-900">
                    <div className="font-semibold">Non puoi inviare questo ordine</div>
                    {user?.impersonatoDa ? (
                      <p className="mt-1">
                        Stai navigando come questo cliente: in impersonazione non si
                        inviano ordini per suo conto. Il carrello resta salvato per lui.
                      </p>
                    ) : (
                      <p className="mt-1">
                        Il tuo profilo operatore può preparare il carrello ma non inoltrarlo.
                        Il carrello resta salvato: chiedi al titolare dell&apos;account di
                        inviarlo, oppure di assegnarti il ruolo «Crea ordine».
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Il finto gateway con il form carta viveva qui. Rimosso il
          15/09/2026: i dati della carta si inseriscono sul dominio Nexi,
          mai sul nostro. Vedi PaymentRedirectPage. */}

      {/* Dialog conferma incongruenze prezzo/stock (07/07/2026) */}
      {mismatchInfo && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="order-mismatch-dialog">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="bg-amber-50 border-b border-amber-200 px-6 py-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-amber-800">Prezzo o disponibilità cambiati</div>
                <div className="text-xs text-amber-700 mt-0.5">
                  Alcuni articoli nel carrello sono cambiati da quando li hai aggiunti. Verifica i valori aggiornati prima di confermare.
                </div>
              </div>
              <button onClick={() => setMismatchInfo(null)} className="text-amber-600 hover:text-amber-800" data-testid="order-mismatch-dialog-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3 max-h-[50vh] overflow-y-auto">
              {mismatchInfo.map(m => (
                <div key={m.sku} className="border rounded-lg p-3" data-testid={`order-mismatch-item-${m.sku}`}>
                  <div className="text-sm font-medium mb-1.5">{m.title || m.sku}</div>
                  <div className="text-xs text-muted-foreground space-y-1">
                    {(m.reason === 'price' || m.reason === 'price_and_stock') && (
                      <div>Prezzo: <span className="line-through">{"\u20AC" + m.requestedPrice.toFixed(2)}</span>{' \u2192 '}<span className="font-semibold text-foreground">{"\u20AC" + m.currentPrice.toFixed(2)}</span></div>
                    )}
                    {m.reason === 'non_vendibile' && (
                      <div className="font-semibold text-red-700">Non più disponibile per l&apos;acquisto online: verrà tolto dall&apos;ordine.</div>
                    )}
                    {m.reason === 'rimosso' && (
                      <div className="font-semibold text-red-700">Articolo non più disponibile: verrà tolto dall&apos;ordine.</div>
                    )}
                    {(m.reason === 'stock' || m.reason === 'price_and_stock') && (
                      <div>Quantità: <span className="line-through">{m.requestedQty} pz</span>{' \u2192 '}<span className="font-semibold text-foreground">{m.availableStock} pz disponibili</span></div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-5 pt-0 flex gap-3">
              <button
                onClick={() => setMismatchInfo(null)}
                className="flex-1 h-11 rounded-lg border text-sm font-medium hover:bg-muted transition-colors"
                data-testid="order-mismatch-cancel"
              >
                Annulla
              </button>
              <button
                onClick={() => { setMismatchInfo(null); void completeOrder(true); }}
                disabled={submitting}
                className="flex-1 h-11 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
                data-testid="order-mismatch-confirm"
              >
                Conferma e procedi
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
